import React, { useState, useEffect } from 'react';
import privateApi from "../../apis/privateApi";
import Notification from "../../components/Notification/Notification";
import ConfirmDialog from "../../components/ConfirmDialog/ConfirmDialog";
import './WardsCreate.css';
import { useNavigate } from 'react-router-dom';

const WardsCreate = () => {

    const navigate = useNavigate();

    // -------------------------
    // FORM STATE
    // -------------------------
    const [name, setName] = useState('');
    const [code, setCode] = useState('');

    const [districtId, setDistrictId] = useState('');
    const [lgaId, setLgaId] = useState('');

    const [districts, setDistricts] = useState([]);
    const [lgas, setLgas] = useState([]);

    const [loading, setLoading] = useState(false);
    const [loadingLgas, setLoadingLgas] = useState(false);

    const [notification, setNotification] = useState({
        show: false,
        message: '',
        type: 'info'
    });

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmToken, setConfirmToken] = useState('');
    const [pendingSubmitData, setPendingSubmitData] = useState(null);

    const handleNotificationClose = () => {
        setNotification(prev => ({ ...prev, show: false }));
    };

    const generateToken = () =>
        Math.random().toString(36).substring(2, 8).toUpperCase();

    // ---------------------------------------------
    // FETCH DISTRICTS
    // ---------------------------------------------
    useEffect(() => {
        const fetchDistricts = async () => {
            try {
                const res = await privateApi.get("/api/senatorial-districts");
                setDistricts(res.data.data || []);
            } catch (err) {
                setNotification({
                    show: true,
                    message: "Failed to load districts",
                    type: "error"
                });
            }
        };

        fetchDistricts();
    }, []);

    // ---------------------------------------------
    // FETCH LGAs BY DISTRICT
    // ---------------------------------------------
    useEffect(() => {
        if (!districtId) {
            setLgas([]);
            return;
        }

        const fetchLgas = async () => {
            try {
                setLoadingLgas(true);
                const res = await privateApi.get(`/api/lgas?senatorialDistrictId=${districtId}`);
                setLgas(res.data.data || []);
            } catch (err) {
                setNotification({
                    show: true,
                    message: "Failed to load LGAs",
                    type: "error"
                });
            } finally {
                setLoadingLgas(false);
            }
        };

        fetchLgas();
    }, [districtId]);

    // ---------------------------------------------
    // SUBMIT
    // ---------------------------------------------
    const handleSubmit = (e) => {
        e.preventDefault();

        if (!districtId) {
            return setNotification({ show: true, message: "Select a district", type: "error" });
        }

        if (!lgaId) {
            return setNotification({ show: true, message: "Select an LGA", type: "error" });
        }

        if (!name || !code) {
            return setNotification({ show: true, message: "Name and code are required", type: "error" });
        }

        const token = generateToken();
        setConfirmToken(token);

        setPendingSubmitData({
            name,
            code,
            lgaId
        });

        setConfirmOpen(true);
    };

    // ---------------------------------------------
    // CONFIRM
    // ---------------------------------------------
    const handleConfirm = async (input) => {

        if (input !== confirmToken) {
            setNotification({
                show: true,
                message: 'Incorrect code entered. Action cancelled.',
                type: 'error'
            });
            setConfirmOpen(false);
            return;
        }

        setConfirmOpen(false);
        setLoading(true);

        try {

            const payload = {
                name: pendingSubmitData.name,
                code: pendingSubmitData.code,
                lgaId: pendingSubmitData.lgaId
            };

            const res = await privateApi.post(`/api/wards`, payload);

            setNotification({
                show: true,
                message: `Ward ${res.data.data.name} created successfully!`,
                type: 'success'
            });

            // RESET
            setName('');
            setCode('');
            setDistrictId('');
            setLgaId('');
            setLgas([]);

        } catch (err) {
            setNotification({
                show: true,
                message: err.response?.data?.message || 'Failed to create ward.',
                type: 'error'
            });
        } finally {
            setLoading(false);
            setPendingSubmitData(null);
        }
    };

    const handleCancel = () => {
        setConfirmOpen(false);
        setPendingSubmitData(null);
    };

    return (
        <div className="wards-create">

            <h2>Create Ward</h2>

            <Notification {...notification} onClose={handleNotificationClose} />

            <form onSubmit={handleSubmit}>

                {/* DISTRICT */}
                <div className="wards-create-form-group">
                    <label>Senatorial District</label>
                    <select value={districtId} onChange={(e) => setDistrictId(e.target.value)} required>
                        <option value="">Select District</option>
                        {districts.map(d => (
                            <option key={d._id} value={d._id}>{d.name}</option>
                        ))}
                    </select>
                </div>

                {/* LGA */}
                <div className="wards-create-form-group">
                    <label>LGA</label>
                    <select value={lgaId} onChange={(e) => setLgaId(e.target.value)} required>
                        <option value="">Select LGA</option>
                        {lgas.map(l => (
                            <option key={l._id} value={l._id}>{l.name}</option>
                        ))}
                    </select>
                    {loadingLgas && <small>Loading LGAs...</small>}
                </div>

                {/* NAME */}
                <div className="wards-create-form-group">
                    <label>Ward Name</label>
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Enter ward name"
                        required
                    />
                </div>

                {/* CODE */}
                <div className="wards-create-form-group">
                    <label>Ward Code</label>
                    <input
                        type="text"
                        value={code}
                        onChange={(e) => setCode(e.target.value.toUpperCase())}
                        placeholder="Enter ward code"
                        required
                    />
                </div>

                {/* ACTIONS */}
                <div className="wards-create-action-buttons">
                    <button type="submit" disabled={loading}>
                        {loading ? 'Creating...' : 'Create Ward'}
                    </button>

                    <button type="button" onClick={() => navigate(-1)}>
                        Back
                    </button>
                </div>

            </form>

            <ConfirmDialog
                show={confirmOpen}
                token={confirmToken}
                action={`You are about to create ward "${name}"`}
                onConfirm={handleConfirm}
                onCancel={handleCancel}
            />

        </div>
    );
};

export default WardsCreate;